import type { Breakpoint } from '@mui/material/styles';
import type { NavSectionProps } from 'src/components/nav-section';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import { varAlpha, hideScrollY } from 'src/theme/styles';

import { Logo } from 'src/components/logo';
import { Scrollbar } from 'src/components/scrollbar';
import { NavSectionMini, NavSectionVertical } from 'src/components/nav-section';

import useAuth from 'src/auth/useAuth';

import { NavToggleButton } from '../components/nav-toggle-button';
import { NavUserFooter } from '../components/nav-user-footer';
import { NavWalletBalanceWidget } from '../components/nav-wallet-balance-widget';
import { OrganizationsPopover } from '../components/organizations-popover';

// ----------------------------------------------------------------------

export type NavVerticalProps = NavSectionProps & {
  isNavMini: boolean;
  layoutQuery: Breakpoint;
  onToggleNav: () => void;
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
};

export function NavVertical({
  sx,
  data,
  slots,
  isNavMini,
  layoutQuery,
  onToggleNav,
  ...other
}: NavVerticalProps) {
  const theme = useTheme();
  const { organizationId } = useAuth();

  const renderNavVertical = (
    <Box
      sx={{
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {slots?.topArea ?? (
        <Box
          sx={{
            pl: 3.5,
            pr: 2,
            pt: 2.5,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 0,
            flexShrink: 0,
          }}
        >
          <Logo />
          <OrganizationsPopover
            sx={{
              color: 'var(--layout-nav-text-primary-color)',
              minWidth: 0,
            }}
          />
        </Box>
      )}

      <Scrollbar fillContent sx={{ flex: '1 1 auto', minHeight: 0 }}>
        <NavSectionVertical data={data} sx={{ px: 2, flex: '1 1 auto' }} {...other} />
      </Scrollbar>

      {organizationId ? <NavWalletBalanceWidget organizationId={organizationId} /> : null}
      {slots?.bottomArea ?? <NavUserFooter />}
    </Box>
  );

  const renderNavMini = (
    <Box
      sx={{
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {slots?.topArea ?? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2.5,
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Logo />
          <OrganizationsPopover
            compact
            sx={{ color: 'var(--layout-nav-text-primary-color)' }}
          />
        </Box>
      )}

      <NavSectionMini
        data={data}
        sx={{
          pb: 2,
          px: 0.5,
          ...hideScrollY,
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
        }}
        {...other}
      />

      {organizationId ? <NavWalletBalanceWidget organizationId={organizationId} compact /> : null}
      {slots?.bottomArea ?? <NavUserFooter compact />}
    </Box>
  );

  return (
    <Box
      sx={{
        top: 0,
        left: 0,
        height: 1,
        display: 'none',
        position: 'fixed',
        flexDirection: 'column',
        minHeight: 0,
        bgcolor: 'var(--layout-nav-bg)',
        zIndex: 'var(--layout-nav-zIndex)',
        width: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
        borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)})`,
        transition: theme.transitions.create(['width'], {
          easing: 'var(--layout-transition-easing)',
          duration: 'var(--layout-transition-duration)',
        }),
        [theme.breakpoints.up(layoutQuery)]: {
          display: 'flex',
        },
        ...sx,
      }}
    >
      <NavToggleButton
        isNavMini={isNavMini}
        onClick={onToggleNav}
        sx={{
          display: 'none',
          [theme.breakpoints.up(layoutQuery)]: {
            display: 'inline-flex',
          },
        }}
      />
      {isNavMini ? renderNavMini : renderNavVertical}
    </Box>
  );
}
